import {
    StyleSheet
} from "react-native";
import type {
    RowCardSkeletonWidthsConstantType,
    RowCardTitleVariantConstantType,
    RowCardRadiusConstantType,
    RowCardColorsConstantType,
    RowCardSkeletonWidths,
    RowCardColorState,
    RowCardColorKeys,
    RowCardColors
} from "./type";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getItemTitleVariant
} from "../item/stylesheet";

export const ROW_CARD_SELECTED_TITLE_VARIANTS: Partial<Record<keyof NCoreUIKit.Typography, keyof NCoreUIKit.Typography>> = {
    labelLargeSize: "titleMediumSize",
    labelMediumSize: "titleSmallSize",
    bodyMediumSize: "titleSmallSize"
};

export const ROW_CARD_COLOR_KEYS: Record<RowCardColorState, RowCardColorKeys> = {
    rest: {
        rightSubTitleColor: "low",
        rightTitleColor: "mid",
        subTitleColor: "mid",
        titleColor: "high",
        iconColor: "mid"
    },
    selected: {
        rightSubTitleColor: "mid",
        rightTitleColor: "mid",
        subTitleColor: "mid",
        titleColor: "high",
        iconColor: "high"
    }
};

export const ROW_CARD_SUBTITLE_SKELETON_WIDTH = "45%";

export const ROW_CARD_TITLE_SKELETON_WIDTH = "60%";

export const ROW_CARD_VALUE_SKELETON_RATIO = 4;

export const ROW_CARD_META_SKELETON_RATIO = 2;

export const ROW_CARD_SPACE_KEY = " ";

export const getRowCardRadius = ({
    borderRadius,
    isInList,
    radiuses,
    borders,
    spaces
}: RowCardRadiusConstantType): number => {
    if(borderRadius !== undefined) {
        return borderRadius;
    }

    if(!isInList) {
        return radiuses.actions;
    }

    return concentric({
        inset: spaces.spacingXs + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions,
        isCapsuleExempt: false
    });
};

export const getRowCardTitleVariant = ({
    titleVariant,
    isSelected
}: RowCardTitleVariantConstantType): keyof NCoreUIKit.Typography => {
    return getItemTitleVariant({
        size: "medium",
        titleVariant,
        isSelected
    });
};

export const getRowCardContainerColor = ({
    isTransparentBackground,
    backgroundColor,
    isDisabled,
    isSelected,
    colors
}: Pick<RowCardColorsConstantType, "isTransparentBackground" | "backgroundColor" | "isDisabled" | "isSelected" | "colors">): string | undefined => {
    if(isSelected && !isDisabled) {
        return colors.content.container.selected;
    }

    if(backgroundColor) {
        return colors.content.container[backgroundColor];
    }

    if(isTransparentBackground) {
        return "transparent";
    }

    return undefined;
};

export const getRowCardColors = ({
    isTransparentBackground,
    rightSubTitleColor,
    backgroundColor,
    rightTitleColor,
    subTitleColor,
    isDisabled,
    isSelected,
    titleColor,
    iconColor,
    colors
}: RowCardColorsConstantType): RowCardColors => {
    const containerColor = getRowCardContainerColor({
        isTransparentBackground,
        backgroundColor,
        isDisabled,
        isSelected,
        colors
    });

    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            rightSubTitleColor: disabledColor,
            rightTitleColor: disabledColor,
            subTitleColor: disabledColor,
            titleColor: disabledColor,
            iconColor: disabledColor,
            iconColorKey: "disabled",
            containerColor
        };
    }

    const currentKeys = ROW_CARD_COLOR_KEYS[isSelected ? "selected" : "rest"];

    const iconColorKey = iconColor ?? currentKeys.iconColor;

    return {
        rightSubTitleColor: colors.content.text[rightSubTitleColor ?? currentKeys.rightSubTitleColor],
        rightTitleColor: colors.content.text[rightTitleColor ?? currentKeys.rightTitleColor],
        subTitleColor: colors.content.text[subTitleColor ?? currentKeys.subTitleColor],
        titleColor: colors.content.text[titleColor ?? currentKeys.titleColor],
        iconColor: colors.content.icon[iconColorKey],
        containerColor,
        iconColorKey
    };
};

export const getRowCardSkeletonWidths = ({
    iconSizes
}: RowCardSkeletonWidthsConstantType): RowCardSkeletonWidths => {
    return {
        rightSubTitle: iconSizes.small * ROW_CARD_META_SKELETON_RATIO,
        rightTitle: iconSizes.small * ROW_CARD_VALUE_SKELETON_RATIO
    };
};

const stylesheet = StyleSheet.create({
    evenlyActions: {
        justifyContent: "flex-end",
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 0
    },
    rightIconContainer: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    rightContent: {
        flexDirection: "column",
        alignItems: "flex-end",
        flexShrink: 1,
        minWidth: 0
    },
    rightSubTitle: {
        fontVariant: [
            "tabular-nums"
        ]
    }
});
export default stylesheet;
